
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { Fauxdown } from '../../../concepts/text/Fauxdown';
import { BoardInfo } from '../../canvas/BoardInfo';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { place } from '../../canvas/commands/OrderCommands';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { TextField } from '../../canvas/fields/TextField';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { SmartBox } from './SmartBox';
import { SmartButton } from './SmartButton';
import { Textbox } from './Textbox';
import { TextGenerator } from './TextGenerator';

export const openUrl = defineCommand(
    'openUrl',
    'Open: URL',
    (r, [url]: [string?]) => {
        window.open(url, '_blank');
    },
    [
        stringStep('url', 'Enter the URL to open:')
    ]
);

export const createLinkButton = defineCommand(
    'createLinkButton',
    'Create: Link Button',
    (r, [url, label]: [string, string?]) => {
        label = label || url;
        if (!url || !label) return;

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        objects.update(() => {
            const button = Physicals.createAtCameraPosition(r, Button);
            button.background = '#3f51b5';
            button.content = label;
            button.commandScript = `openUrl "${url}"`;

            const settings = { ...(button.field('content') as TextField).toTextSettings(), fontSize: 16 };
            const size = TextRuler.measureSize(button.content, settings).add(10);
            button.width = Math.ceil(size.x / 10) * 10;
            button.height = Math.ceil(size.y / 10) * 10;
            dispatch(place, [button.id]);
        });
    },
    [
        stringStep('url', 'Enter the URL:'),
        stringStep('label', 'Enter the button label:', ''),
    ]
);

export const createTextbox = defineCommand(
    'createTextbox',
    'Create: Textbox',
    (r) => {
        Physicals.createAtCameraPosition(r, Textbox, nc => {
            nc.background = '#ffffff';
            nc.width = 240;
            nc.height = 50;
            nc.value = '';
        });
    }
);

export const createSmartBox = defineCommand(
    'createSmartBox',
    'Create: Smart Box',
    (r) => {
        Physicals.createAtCameraPosition(r, SmartBox, nc => {
            nc.width = 240;
            nc.height = 50;
        });
    }
);

export const createSmartButton = defineCommand(
    'createSmartButton',
    'Create: Smart Button',
    (r) => {
        Physicals.createAtCameraPosition(r, SmartButton, nc => {
            nc.width = 250;
            nc.height = 50;
        });
    }
);

export const createCheckbox = defineCommand(
    'createCheckbox',
    'Create: Checkbox',
    (r) => {
        Physicals.createAtCameraPosition(r, Checkbox, nc => {
            nc.width = 120;
            nc.height = 40;
        });
    }
);

export const createTextGenerator = defineCommand(
    'createTextGenerator',
    'Create: Text Generator',
    (r) => {
        Physicals.createAtCameraPosition(r, TextGenerator, nc => {
            nc.background = '#ffffff';
            nc.width = 240;
            nc.height = 50;
            nc.value = '';
        });
    }
);

export const textGeneratorRun = defineCommand(
    'textGeneratorRun',
    'Text Generator: Run',
    async (cc, [subjectId]: [string?]) => {

        const subject = resolveSubjects(cc, subjectId)[0]?.as(TextGenerator);
        if (!subject) return;

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const board = cc.resolve(EditorSession).get(BoardInfo);

        let prompt = subject.prompt;
        const attachments = {} as Record<string, any>;

        const mentions = Fauxdown.parseMentions(prompt);
        for (const { source, value, toId } of mentions) {
            const target = objects.getMaybe(toId, Physical);
            if (!target) throw new Error(`Target ${toId} not found`);
            prompt = prompt.replace(source, `[${value}]`);
            attachments[`[${value}]`] = target.toJson(RelativeTo.Model);
        }

        await api.exec(v2.llm.start(
            board.id,
            subject.id,
            prompt,
            attachments,
        ));
    }
);


export const inControlsCommands = () => inCommands(
    createLinkButton,
    openUrl,
    createSmartBox,
    createSmartButton,
    createCheckbox,
    createTextbox,
    createTextGenerator,
    textGeneratorRun,
);

